import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { curriculumAPI } from '../services/api';

function QRCodeSVG({ url, size = 120 }) {
  const verifyUrl = url || '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg width={size} height={size} viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        <rect width="120" height="120" fill="#fff" rx="8" />
        <rect x="10" y="10" width="30" height="30" rx="4" fill="#1a1a2e" />
        <rect x="14" y="14" width="22" height="22" rx="2" fill="#fff" />
        <rect x="18" y="18" width="14" height="14" rx="1" fill="#1a1a2e" />
        <rect x="80" y="10" width="30" height="30" rx="4" fill="#1a1a2e" />
        <rect x="84" y="14" width="22" height="22" rx="2" fill="#fff" />
        <rect x="88" y="18" width="14" height="14" rx="1" fill="#1a1a2e" />
        <rect x="10" y="80" width="30" height="30" rx="4" fill="#1a1a2e" />
        <rect x="14" y="84" width="22" height="22" rx="2" fill="#fff" />
        <rect x="18" y="88" width="14" height="14" rx="1" fill="#1a1a2e" />
        <rect x="48" y="10" width="8" height="8" fill="#1a1a2e" />
        <rect x="48" y="26" width="8" height="8" fill="#1a1a2e" />
        <rect x="56" y="18" width="8" height="8" fill="#1a1a2e" />
        <rect x="64" y="10" width="8" height="8" fill="#1a1a2e" />
        <rect x="48" y="48" width="8" height="8" fill="#1a1a2e" />
        <rect x="56" y="56" width="8" height="8" fill="#1a1a2e" />
        <rect x="64" y="48" width="8" height="8" fill="#1a1a2e" />
        <rect x="10" y="48" width="8" height="8" fill="#1a1a2e" />
        <rect x="26" y="56" width="8" height="8" fill="#1a1a2e" />
        <rect x="18" y="64" width="8" height="8" fill="#1a1a2e" />
        <rect x="34" y="48" width="8" height="8" fill="#1a1a2e" />
        <rect x="80" y="48" width="8" height="8" fill="#1a1a2e" />
        <rect x="96" y="56" width="8" height="8" fill="#1a1a2e" />
        <rect x="88" y="64" width="8" height="8" fill="#1a1a2e" />
        <rect x="104" y="48" width="8" height="8" fill="#1a1a2e" />
        <rect x="48" y="80" width="8" height="8" fill="#1a1a2e" />
        <rect x="56" y="88" width="8" height="8" fill="#1a1a2e" />
        <rect x="64" y="96" width="8" height="8" fill="#1a1a2e" />
        <rect x="80" y="80" width="8" height="8" fill="#1a1a2e" />
        <rect x="96" y="88" width="8" height="8" fill="#1a1a2e" />
        <rect x="88" y="96" width="8" height="8" fill="#1a1a2e" />
        <rect x="104" y="104" width="8" height="8" fill="#1a1a2e" />
        <rect x="80" y="104" width="8" height="8" fill="#1a1a2e" />
        <rect x="104" y="80" width="8" height="8" fill="#1a1a2e" />
      </svg>
      <span style={{ fontSize: '0.65rem', color: '#888', maxWidth: size, textAlign: 'center', wordBreak: 'break-all' }}>
        {verifyUrl.length > 60 ? verifyUrl.slice(0, 57) + '...' : verifyUrl}
      </span>
    </div>
  );
}

export default function CertificatePage() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [certData, setCertData] = useState(null);
  const [shareLinks, setShareLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await curriculumAPI.getCertificate(domain);
        setCertData(data);
        if (data?.eligible && data?.certificate_id) {
          try {
            const { data: links } = await curriculumAPI.getShareLinks(domain, data.certificate_id);
            setShareLinks(links);
          } catch (_) { /* share links are non-critical */ }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load certificate.');
      } finally {
        setLoading(false);
      }
    })();
  }, [domain]);

  const copyVerifyUrl = useCallback(() => {
    if (!shareLinks?.verify_url) return;
    navigator.clipboard.writeText(shareLinks.verify_url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shareLinks]);

  if (loading) return <div className="page-loading">Loading certificate…</div>;

  if (error) {
    return (
      <div className="domain-page">
        <header className="page-header">
          <button className="btn-text" onClick={() => navigate(-1)}>← Back</button>
          <h1>Certificate</h1>
        </header>
        <p style={{ color: 'var(--error)' }}>{error}</p>
      </div>
    );
  }

  const primaryColor = certData?.primary_color || '#4f46e5';
  const domainLabel = domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (!certData?.eligible) {
    const isCapstonePending = certData?.reason === 'capstone_pending';
    const pct = isCapstonePending ? 100 : (certData?.completion_pct ?? 0);
    return (
      <div className="domain-page">
        <header className="page-header">
          <button className="btn-text" onClick={() => navigate(-1)}>← Back</button>
          <h1>Certificate — {certData?.domain_label || domainLabel}</h1>
        </header>
        <div className="track-card" style={{ maxWidth: 560, margin: '32px auto' }}>
          <h2 style={{ marginBottom: 16 }}>🎓 {isCapstonePending ? 'One last step!' : 'Almost there!'}</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>
            {isCapstonePending
              ? 'Submit and pass your capstone project to earn your certificate.'
              : <>Complete all modules to earn your certificate. You've completed{' '}
                  <strong style={{ color: primaryColor }}>{pct}%</strong>.</>}
          </p>
          <div
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: 8,
              height: 14,
              overflow: 'hidden',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                width: `${Math.min(100, pct)}%`,
                height: '100%',
                background: primaryColor,
                borderRadius: 8,
                transition: 'width 0.4s ease',
              }}
            />
          </div>
          {isCapstonePending ? (
            <button className="btn-primary" onClick={() => navigate(`/domain/${domain}`)}>
              Go to Capstone
            </button>
          ) : (
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              Back to Course
            </button>
          )}
        </div>
      </div>
    );
  }

  const completedDate = certData.completed_at
    ? new Date(certData.completed_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="domain-page certificate-page">
      <style>{`
        @media print {
          .page-header,
          .cert-back-btn,
          .cert-print-btn,
          .cert-actions,
          .cert-verify-section,
          nav,
          .domain-gamification {
            display: none !important;
          }
          body {
            background: #fff !important;
            color: #000 !important;
          }
          .certificate-wrapper {
            box-shadow: none !important;
            border: 2px solid ${primaryColor} !important;
          }
        }
      `}</style>

      <header className="page-header cert-back-btn">
        <button className="btn-text" onClick={() => navigate(-1)}>← Back</button>
        <h1>Certificate</h1>
      </header>

      <div
        className="certificate-wrapper"
        style={{
          maxWidth: 680,
          margin: '32px auto',
          background: '#fff',
          color: '#1a1a2e',
          borderRadius: 12,
          border: `3px solid ${primaryColor}`,
          padding: '48px 56px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
          textAlign: 'center',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}
      >
        {/* Platform header */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              fontSize: '1.2rem',
              fontWeight: 700,
              color: primaryColor,
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            {certData.platform_name}
          </div>
        </div>

        <div
          style={{
            borderTop: `1px solid ${primaryColor}`,
            borderBottom: `1px solid ${primaryColor}`,
            padding: '28px 0',
            marginBottom: 28,
          }}
        >
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: '#1a1a2e',
              marginBottom: 6,
              letterSpacing: 1,
            }}
          >
            Certificate of Completion
          </h1>
          <p style={{ color: '#555', fontSize: '0.95rem', fontStyle: 'italic' }}>
            This certifies that
          </p>
        </div>

        <p
          style={{
            fontSize: '1.7rem',
            fontWeight: 700,
            color: primaryColor,
            margin: '12px 0 8px',
          }}
        >
          {certData.learner_name}
        </p>
        <p style={{ color: '#555', fontSize: '0.95rem', marginBottom: 20 }}>
          has successfully completed
        </p>
        <p
          style={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: '#1a1a2e',
            marginBottom: 4,
          }}
        >
          {certData.track_title}
        </p>
        <p
          style={{
            fontSize: '1rem',
            color: '#444',
            marginBottom: 36,
          }}
        >
          {certData.domain_label}
        </p>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: `1px solid #e0e0e0`,
            paddingTop: 20,
            fontSize: '0.85rem',
            color: '#555',
          }}
        >
          {completedDate && (
            <span>
              <strong>Date:</strong> {completedDate}
            </span>
          )}
          <span style={{ marginLeft: 'auto' }}>
            <strong>ID:</strong>{' '}
            <span
              style={{
                fontFamily: 'monospace',
                background: '#f5f5f5',
                padding: '2px 6px',
                borderRadius: 4,
                color: '#333',
              }}
            >
              {certData.certificate_id}
            </span>
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div
        className="cert-actions"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 12,
          marginTop: 24,
          marginBottom: 16,
        }}
      >
        <button
          className="btn-primary cert-print-btn"
          style={{ minWidth: 180 }}
          onClick={() => window.print()}
        >
          Print Certificate
        </button>

        {shareLinks?.linkedin_url && (
          <button
            className="btn-primary cert-print-btn"
            style={{ minWidth: 180, background: '#0A66C2' }}
            onClick={() => window.open(shareLinks.linkedin_url, '_blank', 'noopener')}
          >
            Share on LinkedIn
          </button>
        )}

        {shareLinks?.badge_url && (
          <a
            href={shareLinks.badge_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary cert-print-btn"
            style={{
              minWidth: 180,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
            }}
          >
            Download Badge
          </a>
        )}
      </div>

      {/* Verification section */}
      {shareLinks?.verify_url && (
        <div
          className="cert-verify-section cert-print-btn"
          style={{
            maxWidth: 680,
            margin: '0 auto 40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            padding: '24px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <QRCodeSVG url={shareLinks.verify_url} size={100} />
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8 }}>
                Scan or visit to verify this certificate
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                <a
                  href={shareLinks.verify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '0.8rem',
                    color: primaryColor,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    maxWidth: 320,
                  }}
                >
                  Verify Certificate
                </a>
                <button
                  onClick={copyVerifyUrl}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {copied ? 'Copied!' : 'Copy URL'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
